console.log("Custom filter loaded");

const PRODUCT_JSON_URL =
  "https://cdn.shopify.com/s/files/1/1014/0424/6355/files/products_index_e9f1d6db-ea0a-4213-9822-ba3e8f9e7cc7.json?v=1789250496";

const sectionEl = document.querySelector('[data-collection-handle]');
const COLLECTION_HANDLE = sectionEl?.dataset.collectionHandle || null;

let products = [];
let filteredProducts = [];
let stockIndex = {};
let productsShown = 300;
const productsPerPage = 300;

let loadingInterval = null;


/* =========================================================
   LOADING ANIMATION
   ========================================================= */

function startLoadingAnimation() {
  const grid = document.getElementById("custom-product-grid");
  if (!grid) return;

  let dotCount = 0;
  grid.textContent = "Loading products";

  loadingInterval = setInterval(() => {
    dotCount = (dotCount + 1) % 4;
    grid.textContent = `Loading products${".".repeat(dotCount)}`;
  }, 400);
}


function stopLoadingAnimation() {
  if (loadingInterval) {
    clearInterval(loadingInterval);
    loadingInterval = null;
  }
}


/* =========================================================
   LOAD PRODUCTS
   ========================================================= */

async function loadProducts() {
  startLoadingAnimation();

  try {
    const productResponse = await fetch(PRODUCT_JSON_URL);

    products = await productResponse.json();

    await loadStockIndex();

    /*
     * Only keep products which exist in Shopify's
     * stock index.
     */
    products = products.filter(
      (p) => stockIndex.hasOwnProperty(p.handle)
    );

    filteredProducts = [...products];

    populateFilters();

    await applyFilters();

  } finally {
    stopLoadingAnimation();
  }
}


/* =========================================================
   LOAD SHOPIFY STOCK / PRICE / SKU / CONDITIONS
   ========================================================= */

async function loadStockIndex() {

  console.log(
    "Loading Shopify stock + price + SKU + conditions...",
    COLLECTION_HANDLE
      ? `(collection: ${COLLECTION_HANDLE})`
      : "(all products)"
  );

  let page = 1;
  let finished = false;

  const baseUrl = COLLECTION_HANDLE
    ? `/collections/${COLLECTION_HANDLE}/products.json`
    : `/products.json`;


  while (!finished) {

    const response = await fetch(
      `${baseUrl}?limit=250&page=${page}`
    );

    const data = await response.json();


    if (
      !data.products ||
      data.products.length === 0
    ) {
      finished = true;
      break;
    }


    data.products.forEach((product) => {

      /*
       * =====================================================
       * AVAILABLE
       * =====================================================
       *
       * Product is considered in stock if at least one
       * variant is available.
       */

      const available =
        product.variants.some(
          (variant) => variant.available
        );


      /*
       * =====================================================
       * PRICE
       * =====================================================
       *
       * Keep existing behaviour:
       * use the highest variant price.
       */

      const maxPrice =
        Math.max(
          ...product.variants.map(
            (variant) =>
              parseFloat(variant.price)
          )
        );


      /*
       * =====================================================
       * SKU
       * =====================================================
       *
       * Your SKUs are sequential numbers representing
       * the order cards were added.
       *
       * We look at every variant belonging to the product
       * and take the largest numeric SKU.
       */

      const variantSkus =
        product.variants
          .map((variant) => {

            if (!variant.sku) return null;

            const match =
              String(variant.sku).match(/\d+/);

            if (!match) return null;

            const number =
              parseInt(match[0], 10);

            return Number.isFinite(number)
              ? number
              : null;
          })
          .filter(
            (sku) => sku !== null
          );


      const maxSku =
        variantSkus.length
          ? Math.max(...variantSkus)
          : 0;


      /*
       * =====================================================
       * CONDITIONS
       * =====================================================
       *
       * We maintain TWO condition lists.
       *
       * conditions:
       *     Conditions which currently have stock.
       *
       * allConditions:
       *     Every condition which exists on the product,
       *     including sold-out variants.
       *
       * conditionPrices:
       *     The price of each condition.
       *
       * Example:
       *
       * {
       *   "near mint": 10.00,
       *   "light play": 8.00,
       *   "moderate play": 6.00
       * }
       */

      const conditionOptions = [];
      const allConditionOptions = [];
      const conditionPrices = {};


      /*
       * Find the option index where the product option
       * is named "Condition".
       */

      let conditionOptionIndex = -1;


      if (Array.isArray(product.options)) {

        conditionOptionIndex =
          product.options.findIndex(
            (option) =>
              String(option.name)
                .trim()
                .toLowerCase() === "condition"
          );

      }


      /*
       * Read every variant.
       */

      if (conditionOptionIndex !== -1) {

        product.variants.forEach(
          (variant) => {

            let condition = null;


            if (conditionOptionIndex === 0) {
              condition = variant.option1;
            }

            if (conditionOptionIndex === 1) {
              condition = variant.option2;
            }

            if (conditionOptionIndex === 2) {
              condition = variant.option3;
            }


            if (!condition) return;


            /*
             * Normalise the condition for price lookups.
             *
             * This means:
             *
             * "Near Mint"
             * "near mint"
             * "NEAR MINT"
             *
             * all refer to the same price.
             */

            const conditionKey =
              String(condition)
                .trim()
                .toLowerCase();


            /*
             * Add to ALL conditions first.
             *
             * This includes sold-out variants.
             */

            if (
              !allConditionOptions.includes(condition)
            ) {

              allConditionOptions.push(
                condition
              );

            }


            /*
             * =================================================
             * CONDITION PRICE
             * =================================================
             *
             * Store the price regardless of whether the
             * variant is currently in stock.
             *
             * This allows the condition filter to display
             * the correct price even when looking at
             * out-of-stock conditions.
             *
             * If there are multiple variants with the same
             * condition, use the highest price.
             */

            const variantPrice =
              parseFloat(variant.price);


            if (
              Number.isFinite(variantPrice)
            ) {

              if (
                !Object.prototype.hasOwnProperty.call(
                  conditionPrices,
                  conditionKey
                ) ||
                variantPrice >
                  conditionPrices[conditionKey]
              ) {

                conditionPrices[conditionKey] =
                  variantPrice;

              }

            }


            /*
             * Only add to the available condition
             * list if this variant is currently available.
             */

            if (!variant.available) return;


            if (
              !conditionOptions.includes(condition)
            ) {

              conditionOptions.push(
                condition
              );

            }

          }
        );

      }


      /*
       * Store everything for this product.
       */

      stockIndex[product.handle] = {

        available,

        price: maxPrice,

        maxSku,

        /*
         * Conditions with stock.
         */
        conditions: conditionOptions,

        /*
         * All conditions, including sold-out ones.
         */
        allConditions: allConditionOptions,

        /*
         * Price for each condition.
         *
         * Keys are lower-case condition names.
         */
        conditionPrices

      };

    });


    page++;

  }


  console.log(
    "Stock + price + SKU + conditions loaded:",
    Object.keys(stockIndex).length
  );

}


/* =========================================================
   CONDITION ORDER
   ========================================================= */

const conditionOrder = [
  "near mint",
  "light play",
  "moderate play",
  "heavy play",
  "damaged"
];


function sortConditions(values) {

  return [
    ...new Set(
      values.filter(Boolean)
    )
  ].sort((a, b) => {

    const aIndex =
      conditionOrder.indexOf(
        String(a)
          .trim()
          .toLowerCase()
      );

    const bIndex =
      conditionOrder.indexOf(
        String(b)
          .trim()
          .toLowerCase()
      );


    /*
     * Both are recognised conditions.
     */
    if (
      aIndex !== -1 &&
      bIndex !== -1
    ) {

      return aIndex - bIndex;

    }


    /*
     * Recognised conditions come first.
     */
    if (aIndex !== -1) return -1;

    if (bIndex !== -1) return 1;


    /*
     * Any unexpected condition names remain
     * alphabetical.
     */

    return String(a).localeCompare(
      String(b)
    );

  });

}


/* =========================================================
   GET DISPLAY PRICE
   =========================================================
   
   Determines which price should be shown on the card.

   No condition selected:
       Uses the existing overall product price.

   Specific condition selected:
       Uses the price of that specific condition.
   ========================================================= */

function getDisplayPrice(product) {

  const stockData =
    stockIndex[
      product.handle
    ];


  if (!stockData) {
    return null;
  }


  const condition =
    document.getElementById(
      "filter-condition"
    )?.value || "";


  /*
   * "All Conditions" / no specific condition selected.
   *
   * Keep the existing product price behaviour.
   */

  if (!condition) {
    return stockData.price;
  }


  /*
   * Normalise the selected condition so it matches
   * the conditionPrices keys.
   */

  const conditionKey =
    String(condition)
      .trim()
      .toLowerCase();


  /*
   * Return the specific condition price.
   */

  if (
    Object.prototype.hasOwnProperty.call(
      stockData.conditionPrices || {},
      conditionKey
    )
  ) {

    return stockData.conditionPrices[
      conditionKey
    ];

  }


  /*
   * Fallback to the normal product price if for
   * some reason no condition-specific price exists.
   */

  return stockData.price;

}


/* =========================================================
   GET PRICE FOR SORTING
   ========================================================= */

function getSortPrice(product) {

  const price =
    getDisplayPrice(product);


  return Number.isFinite(
    Number(price)
  )
    ? Number(price)
    : 0;

}


/* =========================================================
   POPULATE FILTERS
   ========================================================= */

function populateFilters() {

  populateDropdown(
    "filter-language",
    products.map(
      (p) => p.language
    )
  );


  populateDropdown(
    "filter-set",
    products.map(
      (p) => p.set
    )
  );


  populateDropdown(
    "filter-rarity",
    products.map(
      (p) => p.rarity
    )
  );


  populateDropdown(
    "filter-variant",
    products.map(
      (p) => p.variant
    )
  );


  /*
   * CONDITION
   *
   * Use ALL conditions initially, including conditions
   * whose variants are sold out.
   */

  const allConditions = [];


  Object.values(stockIndex).forEach(
    (stockData) => {

      if (
        !Array.isArray(
          stockData.allConditions
        )
      ) {
        return;
      }


      stockData.allConditions.forEach(
        (condition) => {

          if (
            !allConditions.includes(condition)
          ) {

            allConditions.push(
              condition
            );

          }

        }
      );

    }
  );


  populateDropdown(
    "filter-condition",
    allConditions
  );

}


/* =========================================================
   GENERIC DROPDOWN POPULATION
   ========================================================= */

function populateDropdown(
  id,
  values
) {

  const dropdown =
    document.getElementById(id);

  if (!dropdown) return;


  /*
   * Remove any existing generated options.
   *
   * Keep the first "All..." option.
   */

  while (dropdown.options.length > 1) {
    dropdown.remove(1);
  }


  /*
   * Conditions get their own fixed order.
   */

  const uniqueValues =
    id === "filter-condition"
      ? sortConditions(values)
      : [
          ...new Set(
            values.filter(Boolean)
          )
        ].sort();


  uniqueValues.forEach(
    (value) => {

      const option =
        document.createElement(
          "option"
        );

      option.value = value;

      option.textContent = value;

      dropdown.appendChild(
        option
      );

    }
  );

}


/* =========================================================
   UPDATE AVAILABLE FILTER OPTIONS
   ========================================================= */

function updateAvailableFilters() {

  updateDropdownAvailability(
    "filter-language",
    "language"
  );


  updateDropdownAvailability(
    "filter-set",
    "set"
  );


  updateDropdownAvailability(
    "filter-rarity",
    "rarity"
  );


  updateDropdownAvailability(
    "filter-variant",
    "variant"
  );


  updateDropdownAvailability(
    "filter-condition",
    "condition"
  );

}


/* =========================================================
   DROPDOWN AVAILABILITY
   ========================================================= */

function updateDropdownAvailability(
  dropdownId,
  field
) {

  const dropdown =
    document.getElementById(
      dropdownId
    );

  if (!dropdown) return;


  const currentLanguage =
    document.getElementById(
      "filter-language"
    )?.value;


  const currentSet =
    document.getElementById(
      "filter-set"
    )?.value;


  const currentRarity =
    document.getElementById(
      "filter-rarity"
    )?.value;


  const currentVariant =
    document.getElementById(
      "filter-variant"
    )?.value;


  const currentCondition =
    document.getElementById(
      "filter-condition"
    )?.value;


  const currentAvailability =
    document.getElementById(
      "filter-availability"
    )?.value ||
    "in-stock";


  /*
   * Check every option in this dropdown.
   */

  [...dropdown.options].forEach(
    (option) => {

      /*
       * Always show the "All..." option.
       */

      if (option.value === "") {

        option.style.display = "";

        option.disabled = false;

        return;

      }


      let possible =
        [...products];


      /*
       * =====================================================
       * AVAILABILITY
       * =====================================================
       */

      if (
        currentAvailability ===
        "in-stock"
      ) {

        possible =
          possible.filter(
            (product) =>
              stockIndex[
                product.handle
              ]?.available === true
          );

      }


      else if (
        currentAvailability ===
        "out-of-stock"
      ) {

        possible =
          possible.filter(
            (product) =>
              stockIndex[
                product.handle
              ]?.available === false
          );

      }


      /*
       * =====================================================
       * LANGUAGE
       * =====================================================
       */

      if (
        field !== "language" &&
        currentLanguage
      ) {

        possible =
          possible.filter(
            (p) =>
              p.language ===
              currentLanguage
          );

      }


      /*
       * =====================================================
       * SET
       * =====================================================
       */

      if (
        field !== "set" &&
        currentSet
      ) {

        possible =
          possible.filter(
            (p) =>
              p.set ===
              currentSet
          );

      }


      /*
       * =====================================================
       * RARITY
       * =====================================================
       */

      if (
        field !== "rarity" &&
        currentRarity
      ) {

        possible =
          possible.filter(
            (p) =>
              p.rarity ===
              currentRarity
          );

      }


      /*
       * =====================================================
       * VARIANT
       * =====================================================
       */

      if (
        field !== "variant" &&
        currentVariant
      ) {

        possible =
          possible.filter(
            (p) =>
              p.variant ===
              currentVariant
          );

      }


      /*
       * =====================================================
       * CONDITION
       * =====================================================
       *
       * In Stock:
       *     use stocked conditions.
       *
       * Out of Stock:
       *     use all conditions.
       *
       * All:
       *     use all conditions.
       */

      if (
        field !== "condition" &&
        currentCondition
      ) {

        possible =
          possible.filter(
            (product) => {

              const stockData =
                stockIndex[
                  product.handle
                ];

              if (!stockData) {
                return false;
              }


              const conditionList =
                currentAvailability ===
                "in-stock"
                  ? stockData.conditions
                  : stockData.allConditions;


              return conditionList?.some(
                (item) =>
                  String(item)
                    .trim()
                    .toLowerCase() ===
                  String(currentCondition)
                    .trim()
                    .toLowerCase()
              );

            }
          );

      }


      /*
       * =====================================================
       * DOES THIS OPTION EXIST?
       * =====================================================
       */

      let exists = false;


      if (field === "condition") {

        exists =
          possible.some(
            (product) => {

              const stockData =
                stockIndex[
                  product.handle
                ];

              if (!stockData) {
                return false;
              }


              const conditionList =
                currentAvailability ===
                "in-stock"
                  ? stockData.conditions
                  : stockData.allConditions;


              return conditionList?.some(
                (item) =>
                  String(item)
                    .trim()
                    .toLowerCase() ===
                  String(option.value)
                    .trim()
                    .toLowerCase()
              );

            }
          );

      }


      else {

        exists =
          possible.some(
            (p) =>
              p[field] ===
              option.value
          );

      }


      /*
       * Hide unavailable filter options.
       */

      option.style.display =
        exists
          ? ""
          : "none";

      option.disabled =
        !exists;

    }
  );


  /*
   * Make absolutely sure the condition options
   * retain the required order.
   *
   * Hiding options above does not change their order,
   * but this also protects against any other code
   * manipulating the dropdown.
   */

  if (
    field === "condition"
  ) {

    const selectedValue =
      dropdown.value;


    const allOptions =
      [...dropdown.options]
        .filter(
          (option) =>
            option.value !== ""
        );


    const sortedOptions =
      allOptions.sort(
        (a, b) => {

          const aIndex =
            conditionOrder.indexOf(
              String(a.value)
                .trim()
                .toLowerCase()
            );

          const bIndex =
            conditionOrder.indexOf(
              String(b.value)
                .trim()
                .toLowerCase()
            );


          if (
            aIndex !== -1 &&
            bIndex !== -1
          ) {

            return aIndex - bIndex;

          }


          if (aIndex !== -1) return -1;

          if (bIndex !== -1) return 1;


          return String(a.value).localeCompare(
            String(b.value)
          );

        }
      );


    sortedOptions.forEach(
      (option) => {

        dropdown.appendChild(
          option
        );

      }
    );


    dropdown.value =
      selectedValue;

  }


  /*
   * Reset the dropdown if the currently selected value
   * has become unavailable.
   */

  if (
    dropdown.selectedOptions[0]?.style.display ===
    "none"
  ) {

    dropdown.value = "";

  }

}


/* =========================================================
   APPLY FILTERS
   ========================================================= */

async function applyFilters() {

  filteredProducts =
    [...products];


  const language =
    document.getElementById(
      "filter-language"
    )?.value;


  const set =
    document.getElementById(
      "filter-set"
    )?.value;


  const rarity =
    document.getElementById(
      "filter-rarity"
    )?.value;


  const variant =
    document.getElementById(
      "filter-variant"
    )?.value;


  const condition =
    document.getElementById(
      "filter-condition"
    )?.value;


  const availability =
    document.getElementById(
      "filter-availability"
    )?.value ||
    "in-stock";


  const sort =
    document.getElementById(
      "filter-sort"
    )?.value;


  /*
   * =====================================================
   * LANGUAGE
   * =====================================================
   */

  if (language) {

    filteredProducts =
      filteredProducts.filter(
        (p) =>
          p.language ===
          language
      );

  }


  /*
   * =====================================================
   * SET
   * =====================================================
   */

  if (set) {

    filteredProducts =
      filteredProducts.filter(
        (p) =>
          p.set ===
          set
      );

  }


  /*
   * =====================================================
   * RARITY
   * =====================================================
   */

  if (rarity) {

    filteredProducts =
      filteredProducts.filter(
        (p) =>
          p.rarity ===
          rarity
      );

  }


  /*
   * =====================================================
   * VARIANT
   * =====================================================
   */

  if (variant) {

    filteredProducts =
      filteredProducts.filter(
        (p) =>
          p.variant ===
          variant
      );

  }


  /*
   * =====================================================
   * CONDITION
   * =====================================================
   *
   * In Stock:
   *     only stocked conditions count.
   *
   * Out of Stock / All:
   *     any condition belonging to the product counts.
   */

  if (condition) {

    filteredProducts =
      filteredProducts.filter(
        (product) => {

          const stockData =
            stockIndex[
              product.handle
            ];

          if (!stockData) {
            return false;
          }


          const conditionList =
            availability ===
            "in-stock"
              ? stockData.conditions
              : stockData.allConditions;


          return conditionList?.some(
            (item) =>
              String(item)
                .trim()
                .toLowerCase() ===
              String(condition)
                .trim()
                .toLowerCase()
          );

        }
      );

  }


  /*
   * =====================================================
   * AVAILABILITY
   * =====================================================
   */

  if (
    availability ===
    "in-stock"
  ) {

    filteredProducts =
      filteredProducts.filter(
        (product) =>
          stockIndex[
            product.handle
          ]?.available === true
      );

  }


  if (
    availability ===
    "out-of-stock"
  ) {

    filteredProducts =
      filteredProducts.filter(
        (product) =>
          stockIndex[
            product.handle
          ]?.available === false
      );

  }


  /*
   * =====================================================
   * SORT: PRICE HIGH → LOW
   * =====================================================
   *
   * IMPORTANT:
   * If a condition is selected, this sorts using
   * the price of that specific condition.
   */

  if (
    sort ===
    "price-desc"
  ) {

    filteredProducts.sort(
      (a, b) =>
        getSortPrice(b) -
        getSortPrice(a)
    );

  }


  /*
   * =====================================================
   * SORT: PRICE LOW → HIGH
   * =====================================================
   *
   * IMPORTANT:
   * If a condition is selected, this sorts using
   * the price of that specific condition.
   */

  if (
    sort ===
    "price-asc"
  ) {

    filteredProducts.sort(
      (a, b) =>
        getSortPrice(a) -
        getSortPrice(b)
    );

  }


  /*
   * =====================================================
   * SORT: CARD NUMBER LOW → HIGH
   * =====================================================
   */

  if (
    sort ===
    "title"
  ) {

    filteredProducts.sort(
      (a, b) =>
        a.title.localeCompare(
          b.title
        )
    );

  }


  /*
   * =====================================================
   * SORT: CARD NUMBER HIGH → LOW
   * =====================================================
   */

  if (
    sort ===
    "title-desc"
  ) {

    filteredProducts.sort(
      (a, b) =>
        b.title.localeCompare(
          a.title
        )
    );

  }


  /*
   * =====================================================
   * SORT: NEWEST ADDED
   * =====================================================
   */

  if (
    sort ===
    "sku-desc"
  ) {

    filteredProducts.sort(
      (a, b) =>
        (
          stockIndex[
            b.handle
          ]?.maxSku ?? 0
        ) -
        (
          stockIndex[
            a.handle
          ]?.maxSku ?? 0
        )
    );

  }


  productsShown =
    productsPerPage;


  updateAvailableFilters();

  renderProducts();

}


/* =========================================================
   HIGH RES IMAGE
   ========================================================= */

function getHighResImage(
  url,
  width = 800
) {

  if (!url) return url;

  const urlObj =
    new URL(url);

  urlObj.searchParams.set(
    "width",
    width
  );

  return urlObj.toString();

}


/* =========================================================
   RENDER PRODUCTS
   ========================================================= */

function renderProducts() {

  const grid =
    document.getElementById(
      "custom-product-grid"
    );

  if (!grid) return;


  grid.innerHTML = "";


  const visibleProducts =
    filteredProducts.slice(
      0,
      productsShown
    );


  visibleProducts.forEach(
    (product) => {

      /*
       * Get the price based on the currently
       * selected condition.
       *
       * All Conditions:
       *     overall product price
       *
       * NM:
       *     NM variant price
       *
       * LP:
       *     LP variant price
       *
       * etc.
       */

      const price =
        getDisplayPrice(product);


      const isAvailable =
        stockIndex[
          product.handle
        ]?.available;


      const card =
        document.createElement(
          "div"
        );


      card.className =
        "custom-product-card";


      card.innerHTML = `
        <a href="${product.url}">
          <div class="custom-product-image-wrapper">

            <img
              src="${getHighResImage(product.image, 600)}"
              srcset="${getHighResImage(product.image, 400)} 400w,
                      ${getHighResImage(product.image, 800)} 800w,
                      ${getHighResImage(product.image, 1200)} 1200w"
              sizes="(max-width: 749px) 45vw, 16vw"
              alt="${product.title}"
              loading="lazy"
              class="custom-product-image"
            >

            ${
              !isAvailable
                ? '<span class="custom-sold-out-badge">Sold out</span>'
                : ""
            }

            ${
              isAvailable
                ? `
                  <button
                    type="button"
                    class="custom-quick-add-btn"
                    data-product-url="${product.url}"
                    aria-label="Quick add ${product.title}"
                  >
                    <span class="svg-wrapper add-to-cart-icon">
                      ${window.horizonAddToCartIcon || ""}
                    </span>
                  </button>
                `
                : ""
            }

          </div>

          <h3 class="custom-product-title">
            ${product.title}
          </h3>

          <p class="custom-product-price">
            ${
              price != null
                ? `£${Number(price).toFixed(2)}`
                : ""
            }
          </p>

        </a>
      `;


      grid.appendChild(card);

    }
  );


  updateCount();

  updateLoadMore();

}


/* =========================================================
   COUNT
   ========================================================= */

function updateCount() {

  const counter =
    document.getElementById(
      "custom-product-count"
    );

  if (!counter) return;


  counter.textContent =
    `${filteredProducts.length} cards`;

}


/* =========================================================
   LOAD MORE
   ========================================================= */

function updateLoadMore() {

  const button =
    document.getElementById(
      "load-more-products"
    );

  if (!button) return;


  button.style.display =
    productsShown >=
    filteredProducts.length
      ? "none"
      : "block";

}


/* =========================================================
   QUICK ADD MODAL
   ========================================================= */

const modal =
  document.getElementById(
    "quick-add-modal"
  );


const modalBody =
  document.getElementById(
    "quick-add-body"
  );


const modalClose =
  document.getElementById(
    "quick-add-close"
  );


const modalOverlay =
  document.getElementById(
    "quick-add-overlay"
  );


function openQuickAddModal(
  productUrl
) {

  if (!modal || !modalBody) return;


  document.body.style.overflow =
    "hidden";


  modalBody.innerHTML =
    "<p style='padding:20px; text-align:center;'>Loading card details...</p>";


  modal.classList.add(
    "is-active"
  );


  modal.setAttribute(
    "aria-hidden",
    "false"
  );


  fetch(productUrl)

    .then(
      (res) =>
        res.text()
    )

    .then(
      (html) => {

        const parser =
          new DOMParser();


        const doc =
          parser.parseFromString(
            html,
            "text/html"
          );


        const productSection =
          doc.querySelector(
            ".product-section"
          ) ||
          doc.querySelector(
            "main"
          ) ||
          doc.body;


        modalBody.innerHTML =
          productSection.innerHTML;


        /*
         * Re-execute inline scripts.
         */

        const scripts =
          modalBody.querySelectorAll(
            "script"
          );


        scripts.forEach(
          (oldScript) => {

            const newScript =
              document.createElement(
                "script"
              );


            Array.from(
              oldScript.attributes
            ).forEach(
              (attr) =>
                newScript.setAttribute(
                  attr.name,
                  attr.value
                )
            );


            newScript.appendChild(
              document.createTextNode(
                oldScript.innerHTML
              )
            );


            oldScript.parentNode.replaceChild(
              newScript,
              oldScript
            );

          }
        );


        /*
         * Initialize condition/copy filter.
         */

        setTimeout(
          () => {

            if (
              typeof window.initConditionCopyFilter ===
              "function"
            ) {

              window.initConditionCopyFilter(
                modalBody
              );

            }


            /*
             * Force change event on pre-checked
             * Condition input.
             */

            const activeCondition =
              modalBody.querySelector(
                '[name*="Condition"]:checked, select[name*="Condition"]'
              );


            if (activeCondition) {

              activeCondition.dispatchEvent(
                new Event(
                  "change",
                  {
                    bubbles: true
                  }
                )
              );

            }

          },
          50
        );

      }
    )

    .catch(
      (err) => {

        console.error(
          "Failed loading product HTML:",
          err
        );


        modalBody.innerHTML =
          "<p style='padding:20px; text-align:center;'>Failed to load product details.</p>";

      }
    );

}


function closeQuickAddModal() {

  if (!modal) return;


  document.body.style.overflow =
    "";

  document.body.style.position =
    "";


  document.body.classList.remove(
    "overflow-hidden",
    "modal-open",
    "is-active"
  );


  document.documentElement.style.overflow =
    "";


  modal.classList.remove(
    "is-active"
  );


  modal.setAttribute(
    "aria-hidden",
    "true"
  );


  if (modalBody) {

    modalBody.innerHTML =
      "";

  }

}


if (modalClose) {

  modalClose.addEventListener(
    "click",
    closeQuickAddModal
  );

}


if (modalOverlay) {

  modalOverlay.addEventListener(
    "click",
    closeQuickAddModal
  );

}


document.addEventListener(
  "keydown",
  (e) => {

    if (
      e.key === "Escape" &&
      modal?.classList.contains(
        "is-active"
      )
    ) {

      closeQuickAddModal();

    }

  }
);


/* =========================================================
   EVENT DELEGATION
   ========================================================= */

document.addEventListener(
  "click",
  (event) => {

    /*
     * QUICK ADD
     */

    const quickAddBtn =
      event.target.closest(
        ".custom-quick-add-btn"
      );


    if (quickAddBtn) {

      event.preventDefault();

      event.stopPropagation();


      const productUrl =
        quickAddBtn.dataset.productUrl;


      openQuickAddModal(
        productUrl
      );


      return;

    }


    /*
     * LOAD MORE
     */

    if (
      event.target.id ===
      "load-more-products"
    ) {

      productsShown +=
        productsPerPage;


      renderProducts();


      return;

    }


    /*
     * CLEAR FILTERS
     */

    if (
      event.target.id ===
      "clear-filters"
    ) {

      document.getElementById(
        "filter-language"
      ).value = "";


      document.getElementById(
        "filter-set"
      ).value = "";


      document.getElementById(
        "filter-rarity"
      ).value = "";


      document.getElementById(
        "filter-variant"
      ).value = "";


      document.getElementById(
        "filter-condition"
      ).value = "";


      document.getElementById(
        "filter-availability"
      ).value =
        "in-stock";


      document.getElementById(
        "filter-sort"
      ).value =
        "price-desc";


      applyFilters();

    }

  }
);


/* =========================================================
   FILTER CHANGE
   ========================================================= */

document.addEventListener(
  "change",
  (event) => {

    if (
      event.target.matches(
        ".custom-filters select"
      )
    ) {

      applyFilters();

    }

  }
);


/* =========================================================
   INITIAL LOAD
   ========================================================= */

loadProducts();


/* =========================================================
   MOBILE BACK TO TOP
   ========================================================= */

const backToTopBtn =
  document.getElementById(
    "mobile-back-to-top"
  );


if (backToTopBtn) {

  window.addEventListener(
    "scroll",
    () => {

      if (
        window.scrollY > 300
      ) {

        backToTopBtn.classList.add(
          "is-visible"
        );

      } else {

        backToTopBtn.classList.remove(
          "is-visible"
        );

      }

    }
  );


  backToTopBtn.addEventListener(
    "click",
    () => {

      window.scrollTo({
        top: 0,
        behavior: "smooth"
      });

    }
  );

}